// Maps a failed clear-unconfirmed-charge POST to the inline copy shown in the
// dialog. The API answers every refusal with `{ error, message }` (PR 328):
// the copy is chosen by the `error` code, never by the sentence, so the admin
// sees our own wording and nothing internal (#439).
import { asApiFailure, parseFailureBody } from "@utils/apiFailure";

const GENERIC_FAILURE =
    "Something went wrong while clearing the charge. Please try again.";

// PR 240 (#382 review F6): the held columns are concurrency tokens, so a clear
// racing the vendor's fraud webhook (or another admin) answers 409
// concurrent_clear — the charge was already resolved and this clear changed
// nothing. The page reloads the list on every refusal and shows this only
// once that reload has answered: the reloaded copy only when it succeeded.
const CONFLICT_FAILURE =
    "This charge was already resolved — by another admin or by Authorize.Net — so nothing was changed. The list has been reloaded to show its current state.";

const CONFLICT_UNREFRESHED_FAILURE =
    "This charge was already resolved elsewhere, so nothing was changed, but the list couldn't be refreshed. Reload the page to see its current state.";

// A refusal whose code this build doesn't know, or that carries none. It is a
// refusal, not a transient failure: the same request gets the same answer, so
// no retry copy.
const REFUSED_FAILURE =
    "This charge couldn't be cleared: Authorize.Net or our records refused this. Check the charge in Authorize.Net before trying again.";

// The clear routes are admin-only server-side, but the client only auth-gates.
// A 401/403 is not transient — "try again" copy would send a non-admin in
// circles, so this deliberately carries no retry language.
const PERMISSION_FAILURE = "You don't have permission to clear charges.";

const CONCURRENT_CLEAR = "concurrent_clear";

// One copy per refusal code. A Map, so an inherited object key ("toString")
// is never mistaken for a code. already_recorded is a 409 but not a race: the
// admin's transaction id is already on a charge.
const REFUSALS: ReadonlyMap<string, string> = new Map([
    [
        "held_for_review",
        "This charge is held for review at Authorize.Net, so it can't be recorded as settled. Clear it as released if the hold settled, or as voided if it was voided.",
    ],
    [
        "no_held_transaction",
        "No held transaction is recorded for this charge, so it can't be released. Reconcile it against Authorize.Net: if it settled, clear it as settled with its transaction ID; if nothing settled, clear it as voided.",
    ],
    [
        "transaction_unverified",
        "Authorize.Net couldn't confirm this transaction, so nothing was recorded. Check the transaction ID in Authorize.Net, then try again.",
    ],
    [
        "transaction_not_captured",
        "Authorize.Net doesn't show this transaction as captured or settled, so it can't be recorded as a payment. Nothing was recorded. Check its status in Authorize.Net.",
    ],
    [
        "transaction_mismatch",
        "This transaction doesn't match the charge: its type, customer or card, amount, or time is different. Nothing was recorded. Check the transaction ID in Authorize.Net.",
    ],
    [
        "plan_missing",
        "The membership plan for this activation couldn't be found, so the payment can't be recorded. Nothing was recorded.",
    ],
    [
        "membership_cancelled",
        "This organization's membership is cancelled. Reactivate the membership before recording the activation charge. Nothing was recorded.",
    ],
    [
        "period_ended",
        "The organization's billing period has already ended, so this payment can't be recorded here. Reconcile the transaction manually against Authorize.Net. Nothing was recorded.",
    ],
    [
        "already_recorded",
        "This transaction ID is already recorded against a charge, so it can't be recorded again. Nothing was changed. Check the transaction ID in Authorize.Net.",
    ],
]);

const nonBlank = (value: unknown): string | undefined =>
    typeof value === "string" && value.trim() !== "" ? value : undefined;

// A malformed request gets the standard validation response, which has no
// code: its `errors` value is a field → messages map, and the first message of
// the first field is the most useful single line.
const firstValidationError = (detail: string | undefined): string | undefined => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(detail ?? "");
    } catch {
        return undefined;
    }

    if (typeof parsed !== "object" || parsed === null) {
        return undefined;
    }

    const { errors } = parsed as Record<string, unknown>;
    if (typeof errors !== "object" || errors === null) {
        return undefined;
    }

    const [first] = Object.values(errors);

    return nonBlank(Array.isArray(first) ? first[0] : first);
};

// `listReloaded`: the page's reload after this refusal succeeded. Only the
// conflict copy depends on it; it defaults to false so the copy never claims
// a reload nobody confirmed.
export const clearChargeErrorMessage = (
    error: unknown,
    listReloaded = false,
): string => {
    const failure = asApiFailure(error);

    if (failure?.status === 401 || failure?.status === 403) {
        return PERMISSION_FAILURE;
    }

    if (failure?.status !== 400 && failure?.status !== 409) {
        return GENERIC_FAILURE;
    }

    const code = parseFailureBody(failure.detail)?.error ?? "";

    if (code === CONCURRENT_CLEAR) {
        return listReloaded ? CONFLICT_FAILURE : CONFLICT_UNREFRESHED_FAILURE;
    }

    const validation =
        failure.status === 400 && code === ""
            ? firstValidationError(failure.detail)
            : undefined;

    return REFUSALS.get(code) ?? validation ?? REFUSED_FAILURE;
};

// #469: a fee's concurrent_clear is not always "already resolved". The API
// answers it when the order's unconfirmed fee changed since the list loaded
// (another admin cleared it, or the charge that claimed the order finished or
// was held meanwhile) and when Released or Settled finds a fee already
// recorded on the order (a lost race). Nothing from this clear was recorded.
const FEE_CONFLICT_FAILURE =
    "This fee charge changed before your clear was saved: another admin may have resolved it, the charge may have finished, or the order already records a fee. Nothing from this clear was recorded. The list has been reloaded to show its current state.";

const FEE_CONFLICT_UNREFRESHED_FAILURE =
    "This fee charge changed before your clear was saved, or the order already records a fee, so nothing from this clear was recorded, but the list couldn't be refreshed. Reload the page to see its current state.";

// The fee's settled check also compares the description the charge was sent
// with, which names its order (API PR 370 review finding 8).
const FEE_MISMATCH_FAILURE =
    "This transaction doesn't match the fee charge: its type, customer or card, amount, time, or order is different. Nothing was recorded. Check the transaction ID in Authorize.Net.";

// The returned-payment fee clear's copy: its own for the two codes above,
// otherwise exactly the membership clears'.
export const clearFeeChargeErrorMessage = (
    error: unknown,
    listReloaded = false,
): string => {
    const failure = asApiFailure(error);
    const code = parseFailureBody(failure?.detail)?.error;

    if (failure?.status === 409 && code === CONCURRENT_CLEAR) {
        return listReloaded
            ? FEE_CONFLICT_FAILURE
            : FEE_CONFLICT_UNREFRESHED_FAILURE;
    }

    if (failure?.status === 400 && code === "transaction_mismatch") {
        return FEE_MISMATCH_FAILURE;
    }

    return clearChargeErrorMessage(error, listReloaded);
};
